import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppLayout from '../../../component/appLayout';
import { ActionBlock, EmptyStateWrapper, HeaderBlock, LeftBlock, TitleDiv } from './styles';
import DisplayFilterDropdown from '../../../component/displayFilterDropdown';
import { rootStore } from '../../../mobx/rootStore';
import { Dropdown, DropdownItem } from '../../../component/dropdown';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import DeleteModal from '../../../component/modals/deleteModal';
import Modal from '../../../component/modals/modal';
import ViewCard from '../../../component/viewCard';
import TaskGrid from '../../project/tasks/taskGrid';
import {
  EMPTY_STATE_SIZE,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  ICON_COLORS,
  PROJECT_ICON_TYPE,
  TASK_LIST_TYPE
} from '../../../global/constants';
import TaskPage from '../../project/tasks/taskPage';
import ProjectIconAvatar from '../../../component/projectIconAvatar';
import { useMobileDevice } from '../../../global/useMobile';
import IconButton from '../../../component/button/iconButton';
import { getViewList, removeView, removeViewUser } from '../../../services/viewServices';
import ShareViewModal from '../../../component/modals/shareViewModal';
import { ROUTES } from '../../../routes/urls';
import { RawView } from '../../../mobx/interfaces/view';
import ResponsiveHeader from '../../../component/responsiveHeader';
import BottomSubNavigation from '../../../component/bottomNavigation/subNavigation';
import BottomNavigationItem from '../../../component/bottomNavigation/subNavigation/bottomNavigationItem';
import EmptyStateV2 from '../../../component/emptyState';

interface ViewParams {
  viewId: string;
}

const ViewDetails = () => {
  //Mobx stores variables
  const { viewStore, workspaceStore, currentUser } = rootStore;
  const { currentView, createViewInput, viewTasks, views, loading } = viewStore;
  const { currentWorkspace } = workspaceStore;

  //other variables
  const { viewId } = useParams<ViewParams>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
  const location = useLocation();
  const history = useHistory();
  const selectedView = viewStore.views.find((view) => view.id === viewId);
  const isMobile = useMobileDevice();

  const isEdit = useMemo(
    () => new URLSearchParams(location.search).get('isEdit') === 'true' || viewId === 'new-view',
    [location.search, viewId]
  );

  const loadView = useCallback(async () => {
    if (!viewId || !currentWorkspace?.id || viewId === 'new-view' || !viewStore.views) return;
    try {
      if (viewStore.views?.find((view) => view.id === viewId)) {
        viewStore.setCurrentView(viewId);
      } else {
        await getViewList();
        viewStore.setCurrentView(viewId);
      }
    } catch (err) {
      console.log('error', err);
    }
  }, [viewId, currentWorkspace?.id, viewStore]);

  useEffect(() => {
    loadView();
  }, [loadView]);

  const onEditClick = useCallback(() => {
    history.push(`${ROUTES.VIEW_ROUTE.replace(':viewId', viewId)}?isEdit=true`);
  }, [history, viewId]);

  const onDeleteClick = useCallback(
    async (viewId: string) => {
      setSelectedViewId(viewId);
      setShowDeleteModal(true);
    },
    [setSelectedViewId, setShowDeleteModal]
  );

  const onShareClick = useCallback((e?: React.MouseEvent<Element, MouseEvent>) => {
    e?.stopPropagation();
    setShowShareModal(true);
  }, []);

  const handleCloseShareModal = useCallback(() => {
    setShowShareModal(false);
  }, []);

  const getDropdownItems = useMemo(() => {
    if (selectedView?.isReadOnly) {
      return [{ label: 'Leave', iconName: 'exit-icon' }];
    } else {
      return [
        { label: 'Edit', iconName: 'edit-icon', onClick: onEditClick },
        { label: 'Share', iconName: 'share-icon', onClick: onShareClick },
        { label: 'Delete', iconName: 'updated-delete-icon', onClick: onDeleteClick }
      ];
    }
  }, [onDeleteClick, onEditClick, onShareClick, selectedView?.isReadOnly]);

  const onClickDropdownItem = useCallback(
    (label: string) => {
      if (label === 'Delete') {
        setShowDeleteModal(true);
        onDeleteClick(viewId);
      } else if (label === 'Share') {
        onShareClick();
      } else if (label === 'Edit') {
        onEditClick();
      } else if (label === 'Leave') {
        removeViewUser({ users: [currentUser?.id] }, viewId);
        history.push(ROUTES.VIEWS);
      }
    },
    [currentUser?.id, history, onDeleteClick, onEditClick, onShareClick, viewId]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedViewId) return;
    const result = await removeView(selectedViewId);
    if (result) {
      rootStore.viewStore.setViews(views.filter((view) => view.id !== selectedViewId) as RawView[]);
      setShowDeleteModal(false);
      setSelectedViewId(null);
      history.push(ROUTES.VIEWS);
    }
  }, [history, selectedViewId, views]);

  const handleCloseModal = useCallback(() => {
    setShowDeleteModal(false);
  }, [setShowDeleteModal]);

  const isEmpty = useMemo(() => {
    // Check if all tasks in filteredTasks are empty
    const isFilteredTasksEmpty = viewTasks.filteredTasks.every(
      (task) =>
        (!task.tasks?.length || task.tasks.length === 0) &&
        (!task.completedTasks?.length || task.completedTasks.length === 0)
    );

    // Check if otherTasks are empty
    const isOtherTasksEmpty =
      (!viewTasks.otherTasks?.tasks?.length || viewTasks.otherTasks.tasks.length === 0) &&
      (!viewTasks.otherTasks?.completedTasks?.length || viewTasks.otherTasks.completedTasks.length === 0);

    // Return true only if both filteredTasks and otherTasks are empty
    return isFilteredTasksEmpty && isOtherTasksEmpty;
  }, [viewTasks]);

  const getTaskView = useMemo(() => {
    const view = createViewInput?.id
      ? createViewInput?.displayFilters?.taskViewType
      : currentView?.displayFilters?.taskViewType;

    if (isEmpty) {
      return (
        <EmptyStateWrapper>
          <EmptyStateV2
            size={EMPTY_STATE_SIZE.MEDIUM}
            title={'No tasks matching the filters'}
            imageUrl={'mytask_empty'}
            isAlignedCenter={true}
            className='empty-state'
          />
        </EmptyStateWrapper>
      );
    }

    if (view === TASK_LIST_TYPE.TASK_GRID) {
      return <TaskGrid loadData={loadView} isView={true} disableDragAndDrop={true} />;
    }
    // if (currentView?.displayFilters?.taskViewType === TASK_LIST_TYPE.CALENDAR_VIEW) {
    //   return (
    //     <CalendarView
    //       loadData={() => {}}
    //       weekWiseTasks={[]}
    //       loadWeekWiseTasks={() => {}}
    //       currentDate={new Date()}
    //       // isView={true}
    //     />
    //   );
    // }
    return (
      <TaskPage
        loadData={loadView}
        propsLoading={false}
        isView={true}
        showProjectName={true}
        hiddenTask={viewTasks?.hiddenTasks}
        isCreateView={isEdit}
      />
    );
  }, [
    createViewInput?.id,
    createViewInput?.displayFilters?.taskViewType,
    currentView?.displayFilters?.taskViewType,
    isEmpty,
    loadView,
    viewTasks?.hiddenTasks,
    isEdit
  ]);

  const moreIcon = useMemo(
    () => (
      <Dropdown
        modalTitle='Manage View'
        content={
          isMobile ? (
            <BottomNavigationItem iconName='three-dots-horizontal-icon' />
          ) : (
            <IconButton
              iconName='three-dots-horizontal-icon'
              size={ICON_BUTTON_SIZE.MEDIUM}
              variant={ICON_BUTTON_TYPE.TERTIARY}
            />
          )
        }
        trigger='click'>
        {getDropdownItems.map((item) => (
          <DropdownItem
            key={item.label}
            label={item.label}
            iconName={item.iconName}
            iconSize={16}
            tone={item.label === 'Delete' ? 'critical' : 'primary'}
            iconViewBox={item.label === 'Share' ? '0 0 16 16' : '0 0 18 18'}
            onClick={() => onClickDropdownItem(item.label)}
          />
        ))}
      </Dropdown>
    ),
    [getDropdownItems, isMobile, onClickDropdownItem]
  );

  const renderFilterDropdown = useMemo(() => {
    return <DisplayFilterDropdown isModal={isMobile} />;
  }, [isMobile]);

  const renderResponsiveHeader = useMemo(() => {
    return (
      <ResponsiveHeader
        title={isEdit ? (viewId === 'new-view' ? 'Create New View' : 'Edit View') : currentView?.name || ''}
        subTitle='Views'
        noMargin={true}
        leftElement={isEdit ? null : renderFilterDropdown}
        isViewDetail={!isEdit}
        applyDottedText={!isEdit && !!currentView?.name}
      />
    );
  }, [isEdit, viewId, currentView?.name, renderFilterDropdown]);

  return (
    <>
      <AppLayout isPosition={true}>
        {isEdit ? (
          <>
            {isMobile && renderResponsiveHeader}
            <ViewCard />
          </>
        ) : (
          <>
            {isMobile ? (
              renderResponsiveHeader
            ) : (
              <HeaderBlock>
                <LeftBlock>
                  <TitleDiv>
                    <IconButton
                      iconName='new-back-arrow-icon'
                      size={ICON_BUTTON_SIZE.MEDIUM}
                      variant={ICON_BUTTON_TYPE.TERTIARY}
                      onClick={() => history.push(ROUTES.VIEWS)}
                    />
                    <ProjectIconAvatar
                      type={currentView?.icon?.type ?? PROJECT_ICON_TYPE.DEFAULT}
                      reference={currentView?.icon?.reference || ''}
                      backgroundColor={currentView?.icon?.color ? ICON_COLORS[currentView?.icon?.color] : ''}
                      projectName={currentView?.name || ''}
                      iconSize={isMobile ? 20 : 16}
                      isClickable={false}
                      size={isMobile ? 36 : 26}
                      fontSize={isMobile ? '20px' : '16px'}
                    />
                    <p>{currentView?.name}</p>
                  </TitleDiv>
                </LeftBlock>
                <ActionBlock>
                  {renderFilterDropdown}
                  {moreIcon}
                </ActionBlock>
              </HeaderBlock>
            )}
          </>
        )}
        {getTaskView}
        <Modal open={showDeleteModal} onClose={handleCloseModal} maxWidth={'334px'}>
          <DeleteModal
            modalTitle='Delete View?'
            description='Are you sure you want to delete this view?'
            onClose={handleCloseModal}
            onOk={handleDeleteConfirm}
            buttonText='Delete'
            isPrimaryButton={false}
            isYes={true}
            loading={loading}
          />
        </Modal>
        <Modal open={showShareModal} onClose={handleCloseShareModal} maxWidth={'360px'}>
          <ShareViewModal onClose={handleCloseShareModal} selectedView={selectedView} />
        </Modal>
      </AppLayout>
      <BottomSubNavigation backNavigationPath={ROUTES.VIEWS} rightElement={!isEdit && moreIcon} />
    </>
  );
};

export default observer(ViewDetails);
